'use client'

import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Target,
  Heart,
  Users,
  MapPin,
  Sparkles
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const values = [
  {
    icon: Heart,
    title: '誠実であれ',
    subtitle: 'Be Sincere',
    description: '顧客と社会に対し、常に誠実であることを約束します。美談や偽りに頼らず、真実の価値を追求します。'
  },
  {
    icon: Target,
    title: '主権を尊べ',
    subtitle: 'Respect Sovereignty',
    description: '安易な依存を良しとせず、自らの手で未来を創り出す「独立した航海者」です。パートナーの皆様が「デジタル主権」を確立することを支援します。'
  },
  {
    icon: Heart,
    title: '深い敬意を払え',
    subtitle: 'Show Deep Respect',
    description: '私たちの仕事は、敬意から始まります。地域と企業の文化、そしてそこにいる人々への深い敬意なくして、真の価値創造はありえません。'
  },
  {
    icon: Users,
    title: '共助を力とせよ',
    subtitle: 'Find Strength in Co-creation',
    description: '地域全体の成功こそが、自らの成功だと信じます。競争ではなく「共創」を、独占ではなく「共助」を、私たちの行動の中心に置きます。'
  },
  {
    icon: Sparkles,
    title: '熱狂を生み出せ',
    subtitle: 'Create Passion',
    description: '単なる効率化された未来ではなく、人々が「ここで挑戦したい」と心から思える、熱狂と希望に満ちた未来を創造します。'
  }
]

export function AboutContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-[#BEB0A8] py-12 md:py-16">
        <div
          className="animate-fade-up container"
        >
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 font-['Noto_Sans_JP',_sans-serif] bg-[#292623] text-white">地域にデジタルの誇りを。</Badge>
            <h1 className="font-['Montserrat',_sans-serif] text-[32px] md:text-[46px] font-semibold text-[#292623] tracking-[-0.92px] mb-6">
              企業情報
            </h1>
            <p className="font-['Noto_Sans_JP',_sans-serif] text-[16px] md:text-[18px] text-[#292623]/80">
              テクノロジーの力で地域の課題を解決し、<br />
              誰もが誇れる未来を創造します
            </p>
          </div>
        </div>
      </section>

      {/* ミッション・ビジョン・バリュー */}
      <section className="py-16 md:py-24 bg-[#BEB0A8]">
        <div className="container px-4">
          <div className="max-w-5xl mx-auto space-y-16 mb-12 md:mb-16">
            <FadeUp
              className="text-center"
            >
              <h2 className="font-['Montserrat',_sans-serif] text-[16px] md:text-[18px] font-semibold text-[#292623]/60 mb-4 tracking-wider">MISSION</h2>
              <p className="font-['Shippori_Mincho_B1',_serif] text-[24px] md:text-[40px] text-[#292623] leading-relaxed font-bold tracking-wide">
                デジタルの力で、地域の誇りを、<br className="hidden md:block" />未来の価値に。
              </p>
            </FadeUp>

            <FadeUp delay={0.2}
              className="text-center"
            >
              <h2 className="font-['Montserrat',_sans-serif] text-[16px] md:text-[18px] font-semibold text-[#292623]/60 mb-4 tracking-wider">VISION</h2>
              <p className="font-['Shippori_Mincho_B1',_serif] text-[24px] md:text-[40px] text-[#292623] leading-relaxed font-bold tracking-wide">
                すべての地域が、その文化と誇りに、<br className="hidden md:block" />経済的な価値を見出す社会。
              </p>
            </FadeUp>
          </div>

          {/* バリュー */}
          <FadeUp
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="font-['Montserrat',_sans-serif] text-[16px] md:text-[18px] font-semibold text-[#292623]/60 mb-4 tracking-wider">VALUES</h2>
            <p className="font-['Noto_Sans_JP',_sans-serif] text-[18px] md:text-[24px] text-[#292623] font-medium">
              私たちが約束する行動指針
            </p>
          </FadeUp>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <div className="h-full p-6 bg-white/80 backdrop-blur rounded-lg border border-[#292623]/10">
                    <Icon className="h-10 w-10 text-[#292623] mb-4" />
                    <h3 className="font-['Noto_Sans_JP',_sans-serif] text-[18px] md:text-[20px] text-[#292623] font-bold mb-2">{value.title}</h3>
                    <p className="font-['Montserrat',_sans-serif] text-[12px] text-[#292623]/50 mb-4 italic tracking-wide">{value.subtitle}</p>
                    <p className="font-['Noto_Sans_JP',_sans-serif] text-[13px] md:text-[14px] text-[#292623]/70 leading-relaxed">{value.description}</p>
                  </div>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 会社概要 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
          >
            <h2 className="text-3xl font-bold text-center mb-12">会社概要</h2>
            
            <Card className="max-w-4xl mx-auto">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">会社名</p>
                      <p className="font-medium">株式会社CodeFox</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">設立</p>
                      <p className="font-medium">2022年8月19日</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">代表取締役 / Security Lead</p>
                      <p className="font-medium">進藤 史裕</p>
                      <p className="text-sm text-muted-foreground mt-1">情報処理安全確保支援士（RISS / 登録番号020238）</p>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground mb-1">事業内容</p>
                      <p className="font-medium">地域DXソリューションの開発・提供</p>
                      <p className="text-sm text-muted-foreground mt-1">セキュリティ評価・脆弱性診断</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">本社所在地</p>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium">〒730-0053</p>
                        <p className="font-medium">広島県広島市中区東千田町1丁目1番61号hitoto広島ナレッジスクエア1F</p>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">拠点</p>
                      <div>
                        <p className="font-medium mb-1">Digital Pride Hiroshima</p>
                        <p className="text-sm text-muted-foreground">広島県広島市中区東千田町1丁目1番61号hitoto広島ナレッジスクエア1F</p>
                      </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </FadeUp>
        </div>
      </section>

      {/* 沿革 */}
      {/* <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-center mb-12">沿革</h2>
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <FadeUp delay={index * 0.1}
                  key={index}
                  className="flex gap-6"
                >
                  <div className="shrink-0">
                    <div className="w-24 h-24 bg-primary text-white rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold">{milestone.year}</span>
                    </div>
                  </div>
                  <div className="flex-1 pt-4">
                    <h3 className="text-xl font-semibold mb-2">{milestone.title}</h3>
                    <p className="text-muted-foreground">{milestone.description}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
        </div>
      </section> */}

      {/* 経営陣 */}
      {/* <section className="py-16">
        <div className="container">
          <FadeUp
          >
            <h2 className="text-3xl font-bold text-center mb-12">経営陣</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member, index) => (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="text-center">
                    <CardHeader>
                      <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4"></div>
                      <CardTitle>{member.name}</CardTitle>
                      <CardDescription>{member.role}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{member.bio}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
        </div>
      </section> */}

      {/* 受賞歴 */}
      {/* <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center"
          >
            <Award className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-12">受賞歴</h2>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                {
                  year: '2024',
                  award: '地域DX大賞',
                  organization: '総務省'
                },
                {
                  year: '2023',
                  award: 'グッドデザイン賞',
                  organization: '日本デザイン振興会'
                },
                {
                  year: '2023',
                  award: 'ベストベンチャー100',
                  organization: 'ベンチャー通信社'
                }
              ].map((award, index) => (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card>
                    <CardContent className="p-6 text-center">
                      <p className="text-3xl font-bold text-primary mb-2">{award.year}</p>
                      <p className="font-semibold mb-1">{award.award}</p>
                      <p className="text-sm text-muted-foreground">{award.organization}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
        </div>
      </section> */}

      {/* CTA */}
      {/* <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold mb-4">
              一緒に地域の未来を創りませんか？
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              CodeFoxでは、地域DXに情熱を持つ仲間を募集しています。<br />
              あなたのスキルを地域のために活かしてみませんか。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link href="/careers">
                  採用情報を見る
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/contact">
                  お問い合わせ
                </Link>
              </Button>
            </div>
          </FadeUp>
        </div>
      </section> */}
    </>
  )
}